import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const STARTING_BALANCE = 100000;

function EquityCurve({ trades }) {
  // Calculate equity curve from trades
  const equityData = useMemo(() => {
    if (!trades || trades.length === 0) return { labels: [], values: [], startingBalance: STARTING_BALANCE };

    // Filter to closed trades and sort by exit time (use pnl_usd with fallback to pnl_points)
    const closedTrades = trades
      .filter(t => t.status === 'CLOSED' && t.exit_time && (t.pnl_usd ?? t.pnl_points) !== null)
      .sort((a, b) => new Date(a.exit_time) - new Date(b.exit_time));

    if (closedTrades.length === 0) return { labels: [], values: [], startingBalance: STARTING_BALANCE };

    let balance = STARTING_BALANCE;

    // Start with entry point at starting balance
    const labels = [formatDate(closedTrades[0].entry_time)];
    const values = [STARTING_BALANCE];

    for (const trade of closedTrades) {
      balance += (trade.pnl_usd ?? trade.pnl_points);
      labels.push(formatDate(trade.exit_time));
      values.push(balance);
    }

    return { labels, values, startingBalance: STARTING_BALANCE };
  }, [trades]);

  if (!trades || trades.length === 0) {
    return (
      <div className="bg-dark-surface rounded-lg p-3 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Equity Curve</h3>
        <div className="h-48 sm:h-64 flex items-center justify-center text-gray-400 text-sm">
          No trade data available
        </div>
      </div>
    );
  }

  const closedTradeCount = trades.filter(t => t.status === 'CLOSED').length;

  if (closedTradeCount === 0) {
    return (
      <div className="bg-dark-surface rounded-lg p-3 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Equity Curve</h3>
        <div className="h-48 sm:h-64 flex items-center justify-center text-gray-400 text-sm">
          No closed trades yet
        </div>
      </div>
    );
  }

  const chartData = {
    labels: equityData.labels,
    datasets: [
      {
        label: 'Account Balance',
        data: equityData.values,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderWidth: 2,
        fill: true,
        tension: 0.1,
        pointRadius: 2,
        pointHoverRadius: 5
      },
      {
        label: 'Starting Balance',
        data: equityData.values.map(() => STARTING_BALANCE),
        borderColor: '#6B7280',
        borderWidth: 1,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '#1e2530',
        titleColor: '#e6edf3',
        bodyColor: '#e6edf3',
        borderColor: '#2d3748',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context) => {
            if (context.datasetIndex === 1) return null; // Hide starting balance line in tooltip
            const value = context.raw;
            const pnl = value - STARTING_BALANCE;
            const pnlSign = pnl >= 0 ? '+' : '-';
            return [
              `Balance: $${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              `P/L: ${pnlSign}$${Math.abs(pnl).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            ];
          }
        },
        filter: (tooltipItem) => tooltipItem.datasetIndex === 0
      }
    },
    scales: {
      x: {
        grid: {
          color: '#334155',
          drawBorder: false
        },
        ticks: {
          color: '#9ca3af',
          maxRotation: 0,
          maxTicksLimit: 6
        }
      },
      y: {
        grid: {
          color: '#334155',
          drawBorder: false
        },
        ticks: {
          color: '#9ca3af',
          callback: (value) => `$${value.toLocaleString('en-US')}`
        }
      }
    }
  };

  return (
    <div className="bg-dark-surface rounded-lg p-3 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Equity Curve</h3>
      <div className="h-48 sm:h-64">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
}

function formatDate(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default EquityCurve;
